'use client';

import React from 'react';
import { Layers, X, RefreshCw, Eye } from 'lucide-react';

interface InspectWorkspaceModalProps {
  inspectWorkspaceId: string | null;
  inspectWorkspaceData: { workspace: any; totalBoards: number; boards: any[] } | null;
  loadingBoards: boolean;
  onClose: () => void;
  handleOpenBoardCards: (boardId: string) => void;
}

export const InspectWorkspaceModal: React.FC<InspectWorkspaceModalProps> = ({
  inspectWorkspaceId,
  inspectWorkspaceData,
  loadingBoards,
  onClose,
  handleOpenBoardCards,
}) => {
  if (!inspectWorkspaceId) return null;

  return (
    <div className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-md flex items-center justify-center p-4">
      <div className="bg-[#0D1122] border border-slate-800 rounded-3xl max-w-3xl w-full p-6 space-y-6 shadow-2xl relative max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between border-b border-slate-800 pb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-indigo-500/15 border border-indigo-500/30 text-indigo-400 flex items-center justify-center">
              <Layers className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-base font-black text-white flex items-center gap-2">
                <span>{inspectWorkspaceData?.workspace?.name || 'Workspace'} Boards</span>
                {inspectWorkspaceData && (
                  <span className="px-2.5 py-0.5 bg-indigo-500/20 text-indigo-300 border border-indigo-500/30 rounded-full text-xs font-mono font-bold">
                    Total {inspectWorkspaceData.totalBoards} Boards
                  </span>
                )}
              </h3>
              <p className="text-xs text-slate-400 font-mono">Slug: /{inspectWorkspaceData?.workspace?.slug}</p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white p-1.5 hover:bg-slate-800 rounded-xl transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto pr-1 space-y-3">
          {loadingBoards ? (
            <div className="py-16 text-center text-slate-400 space-y-3">
              <RefreshCw className="w-8 h-8 text-indigo-400 animate-spin mx-auto" />
              <p className="text-xs font-medium">Fetching workspace boards from database...</p>
            </div>
          ) : !inspectWorkspaceData?.boards || inspectWorkspaceData.boards.length === 0 ? (
            <div className="py-16 text-center text-slate-500 border border-dashed border-slate-800 rounded-2xl">
              <Layers className="w-10 h-10 text-slate-600 mx-auto mb-2 opacity-50" />
              <p className="text-xs font-bold text-slate-400">No Boards Created</p>
              <p className="text-[11px] text-slate-500">This workspace has no active task boards yet.</p>
            </div>
          ) : (
            inspectWorkspaceData.boards.map((b) => (
              <div
                key={b.id}
                onClick={() => handleOpenBoardCards(b.id)}
                className="bg-[#060811] border border-slate-800 hover:border-indigo-500/50 rounded-2xl p-4 transition-all flex flex-col sm:flex-row sm:items-center justify-between gap-4 cursor-pointer group"
              >
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <h4 className="text-sm font-bold text-white group-hover:text-indigo-300 transition-colors">{b.title}</h4>
                    {b.isArchived && (
                      <span className="px-2 py-0.5 bg-rose-500/10 border border-rose-500/20 text-rose-400 text-[10px] font-bold rounded">
                        Archived
                      </span>
                    )}
                  </div>
                  {b.description && <p className="text-xs text-slate-400 line-clamp-1">{b.description}</p>}
                  <div className="flex items-center gap-3 text-[11px] text-slate-500 font-mono pt-1">
                    <span>{b.listCount} Lists/Columns</span>
                    <span>•</span>
                    <span>Created {new Date(b.createdAt).toLocaleDateString()}</span>
                  </div>
                </div>

                <div className="flex items-center gap-3 shrink-0">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenBoardCards(b.id);
                    }}
                    className="px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 hover:opacity-90 text-white font-black text-xs rounded-xl shadow-lg shadow-purple-600/20 flex items-center gap-1.5 transition-all cursor-pointer"
                  >
                    <Eye className="w-3.5 h-3.5" />
                    <span>{b.cardCount} Cards Total &rarr;</span>
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};
